// src/contexts/LanguageContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';

// Create the context
const LanguageContext = createContext();

// Default language
const DEFAULT_LANGUAGE = 'en';

/**
 * Language provider component that manages the application's language state
 */
export function LanguageProvider({ children }) {
  // Initialize language from localStorage or use default
  const [language, setLanguage] = useState(() => {
    try {
      const urlLang = new URLSearchParams(window.location.search).get('lang');
      const savedLanguage = localStorage.getItem('language');
      return urlLang || savedLanguage || DEFAULT_LANGUAGE;
    } catch (e) {
      console.error('Error loading language from localStorage:', e);
      return DEFAULT_LANGUAGE;
    }
  });

  // Dictionary state to store loaded translations
  const [dictionary, setDictionary] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Change language function
  const changeLanguage = (newLanguage) => {
    if (!newLanguage) return; // Guard against undefined values
    setLanguage(newLanguage);
    try {
      localStorage.setItem('language', newLanguage);
    } catch (e) {
      console.error('Error saving language to localStorage:', e);
    }
  };

  // Load language dictionary
  useEffect(() => {
    const loadDictionary = async () => {
      setIsLoading(true);
      try {
        // Dynamic import to load the language file
        const languageFile = await import(`../locales/${language}.json`);
        setDictionary(languageFile.default || {});
      } catch (error) {
        console.error(`Failed to load language file for ${language}:`, error);
        
        // If there's an error, try to load the default language
        if (language !== DEFAULT_LANGUAGE) {
          try {
            const defaultLanguageFile = await import(`../locales/${DEFAULT_LANGUAGE}.json`);
            setDictionary(defaultLanguageFile.default || {});
          } catch (fallbackError) {
            console.error(`Failed to load default language file:`, fallbackError);
            setDictionary({}); // Set empty dictionary as fallback
          }
        } else {
          setDictionary({}); // Set empty dictionary as fallback
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadDictionary();
  }, [language]);

  // Translation function with safety checks
  const translate = (key, params = {}) => {
    // Guard against non-string keys
    if (!key || typeof key !== 'string') {
      console.warn(`Invalid translation key: ${key}`);
      return key || '';
    }

    // Guard against missing dictionary
    if (!dictionary || Object.keys(dictionary).length === 0) {
      return key;
    }

    try {
      // Split dot notation path (e.g., "header.title")
      const keys = key.split('.');
      let value = dictionary;

      // Navigate through the keys to find the translation
      for (const k of keys) {
        if (!value || value[k] === undefined) {
          console.warn(`Translation key not found: ${key}`);
          return key;
        }
        value = value[k];
      }

      // Handle string with interpolation parameters
      if (typeof value === 'string' && Object.keys(params).length > 0) {
        return Object.entries(params).reduce(
          (str, [paramKey, paramValue]) => 
            str.replace(new RegExp(`{${paramKey}}`, 'g'), paramValue),
          value
        );
      }

      return value || key;
    } catch (error) {
      console.error(`Error translating key "${key}":`, error);
      return key;
    }
  };

  const supportedLanguages = [
    { code: 'en', name: 'English', flag: '/flags/US.png' },
    { code: 'ko', name: '한국어', flag: '/flags/KR.png' }
  ];

  const contextValue = {
    language,
    changeLanguage,
    translate,
    isLoading,
    supportedLanguages,
    // Add a convenience method to safely get a language name
    getLanguageName: (code) => {
      const lang = supportedLanguages.find(l => l.code === code);
      return lang ? lang.name : code;
    }
  };

  // You could add a loading screen here if needed
  if (isLoading) {
    return <div className="language-loading">Loading...</div>;
  }

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Custom hook to use the language context with safety checks
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    console.warn('useLanguage must be used within a LanguageProvider');
    // Return fallback context to prevent errors
    return {
      language: 'en',
      changeLanguage: () => {},
      translate: (key) => key,
      isLoading: false,
      supportedLanguages: [
        { code: 'en', name: 'English', flag: '/flags/US.png' },
        { code: 'ko', name: '한국어', flag: '/flags/KR.png' }
      ]
    };
  }
  return context;
}

// Shorthand hook for translations only
export function useTranslation() {
  const { translate } = useLanguage();
  return (key, params) => {
    try {
      return translate(key, params);
    } catch (e) {
      console.error('Translation error:', e);
      return key || '';
    }
  };
}